import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import { Table } from "../../../components/SystemComponent/table";
import { Badge } from "../../../components/SystemComponent/Bagde";
import { Modal } from "../../../components/SystemComponent/Modal";
import { Button } from "../../../components/SystemComponent/Button";
import {
  MdFlightTakeoff,
  MdLuggage,
  MdBusiness,
  MdPerson,
  MdEmail,
  MdPhone,
  MdDateRange,
  MdSchedule,
  MdAttachMoney,
  MdDescription,
  MdOutlineHandshake,
  MdRefresh,
  MdCheckCircle,
  MdAdd,
  MdDeleteOutline,
  MdSearch,
  MdAirplanemodeActive,
  MdInventory,
  MdVisibility,
  MdAttachFile,
  MdPictureAsPdf,
  MdInsertDriveFile,
  MdImage,
  MdOpenInNew,
} from "react-icons/md";
import { createApi } from "../../../../core/api";
import { getApiBaseUrl } from "../../../../core/api/http";

// ─── Status Map ──────────────────────────────────────────────────────────────
const SCM_STATUS: Record<number, { label: string; color: string; bg: string }> = {
  1: { label: "New",                  color: "#2563EB", bg: "rgba(37,99,235,.12)" },
  2: { label: "Reviewed",             color: "#0D9488", bg: "rgba(13,148,136,.12)" },
  3: { label: "Matching",             color: "#F59E0B", bg: "rgba(245,158,11,.15)" },
  4: { label: "Operators Contacted", color: "#8B5CF6", bg: "rgba(139,92,246,.15)" },
  5: { label: "Offers Received",      color: "#EC4899", bg: "rgba(236,72,153,.15)" },
  6: { label: "Match Found",          color: "#16A34A", bg: "rgba(22,163,74,.15)" },
  7: { label: "Closed",               color: "#6B7280", bg: "rgba(107,114,128,.15)" },
};

const CHARTER_TYPES: Record<number, { label: string; icon: any; color: string }> = {
  1: { label: "Passenger Charter", icon: MdFlightTakeoff, color: "#3B82F6" },
  2: { label: "Cargo Charter",     icon: MdLuggage,       color: "#F97316" },
};

const OP_TYPES: Record<number, string> = {
  1: "One Way",
  2: "Round Trip",
  3: "Series / Seasonal",
};

const TIME_PREF: Record<number, string> = {
  1: "AM (00:00 - 11:59)",
  2: "PM (12:00 - 23:59)",
  3: "Flexible (No preference)",
};

const DATE_FLEX: Record<number, string> = {
  0: "Exact Dates (No flexibility)",
  1: "±1 Day Flexibility",
  2: "±2 Days Flexibility",
  3: "±3 Days Flexibility",
};

const RATE_BASIS: Record<number, string> = {
  1: "Block Hour (BH)",
  2: "Per Round Trip (RT)",
  3: "Per Flight",
};

const CARGO_TYPES: Record<number, string> = {
  1: "General Cargo",
  2: "E-commerce",
  3: "Perishable",
  4: "Pharma / Medical",
  5: "Dangerous Goods (DGR)",
  6: "Live Animals (AVI)",
  7: "Humanitarian / Relief",
  8: "Valuable Cargo",
  9: "Heavy / Outsized Cargo",
  10: "Mail / Express",
  99: "Other Cargo",
};

const LOADING_TYPES: Record<number, string> = {
  1: "Palletized",
  2: "Loose / Bulk",
  3: "Containerized (ULD)",
  4: "Mixed Loading",
  99: "Other",
};

const SPECIAL_CARGO: Record<number, string> = {
  0: "None",
  1: "Dangerous Goods (DGR)",
  2: "Temperature Controlled",
  3: "High Value Cargo",
  4: "Fragile Cargo",
  5: "Perishable Cargo",
  99: "Other",
};

const WEIGHT_UNITS: Record<number, string> = {
  1: "kg",
  2: "lbs",
};

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const fmtMoney = (amount?: number | null, cur = "USD", basis?: number | null) => {
  if (amount == null) return "—";
  const basisStr = basis ? `(${RATE_BASIS[basis] || ""})` : "";
  return `${cur} ${Number(amount).toLocaleString()} ${basisStr}`.trim();
};

const fmtFileSize = (bytes?: number): string => {
  if (!bytes || bytes === 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const getDocUrl = (f: any): string => {
  const u = typeof f === "string" ? f : (f?.url || f?.path);
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  const baseUrl = getApiBaseUrl().replace(/\/api\/?$/, "");
  const cleanPath = u.startsWith("/") ? u : `/${u}`;
  return `${baseUrl}${cleanPath}`;
};

const BoolBadge = ({ value }: { value?: boolean | null }) => (
  <Badge
    label={value ? "Yes" : "No"}
    color={value ? "#16A34A" : "#64748B"}
    bg={value ? "rgba(22,163,74,.15)" : "rgba(100,116,139,.15)"}
  />
);

const InfoRow = ({ icon: Icon, label, value }: { icon?: any; label: string; value: any }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "24px 230px 1fr",
      alignItems: "center",
      gap: 12,
      padding: "7px 0",
      borderBottom: "1px solid #F1F5F9",
      fontSize: 13,
    }}
  >
    {Icon ? <Icon size={18} color="#2563EB" /> : <div style={{ width: 18 }} />}
    <span style={{ color: "#64748B", fontWeight: 600 }}>{label}</span>
    <span style={{ fontWeight: 600, color: "#1E293B" }}>{value ?? "—"}</span>
  </div>
);

const Card = ({ icon: Icon, title, children }: { icon?: any; title: string; children: React.ReactNode }) => (
  <div
    style={{
      border: "1px solid #E2E8F0",
      borderRadius: 14,
      padding: 16,
      background: "#FFFFFF",
      boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
      marginBottom: 16,
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 14,
        background: "linear-gradient(135deg, #2563EB 0%, #3B82F6 50%, #60A5FA 100%)",
        color: "#fff",
        borderRadius: 8,
        padding: "8px 12px",
      }}
    >
      {Icon && <Icon size={20} />}
      <strong style={{ fontSize: 14 }}>{title}</strong>
    </div>
    {children}
  </div>
);

export const AdminScmList: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<any>(null);

  // Filters
  const [typeFilter, setTypeFilter] = useState<number | "">("");
  const [statusFilter, setStatusFilter] = useState<number | "">("");
  const [searchText, setSearchText] = useState("");

  // Detail Modal
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [newStatus, setNewStatus] = useState<number>(1);
  const [statusNote, setStatusNote] = useState("");
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);

  // Matching
  const [companies, setCompanies] = useState<any[]>([]);
  const [matchCompanyId, setMatchCompanyId] = useState<number | "">("");
  const [matchNotes, setMatchNotes] = useState("");
  const [matchHasOffer, setMatchHasOffer] = useState(false);
  const [matchOfferAmount, setMatchOfferAmount] = useState<number | "">("");
  const [matchOfferCurrency, setMatchOfferCurrency] = useState("EUR");
  const [matchSaving, setMatchSaving] = useState(false);

  // Load List
  // Load Data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const api = createApi();
      const res = await api.adminScm.getAll({
        page: 1,
        pageSize: 100,
        charterType: typeFilter ? Number(typeFilter) : undefined,
        status: statusFilter ? Number(statusFilter) : undefined,
        search: searchText || undefined,
      });
      if (res?.ok && res.data?.data) {
        setData(res.data.data);
      } else if (res?.ok && Array.isArray(res.data)) {
        setData(res.data);
      }
    } catch (e) {
      console.error("Failed to load SCM list", e);
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter, searchText]);

  // Load Stats
  const loadStats = useCallback(async () => {
    try {
      const api = createApi();
      const res = await api.adminScm.getStats();
      if (res?.ok && res.data?.data) {
        setStats(res.data.data);
      } else if (res?.ok && res.data) {
        setStats(res.data);
      }
    } catch (e) {
      console.error("Failed to load stats", e);
    }
  }, []);

  // Load Companies for matching
  const loadCompanies = useCallback(async () => {
    try {
      const api = createApi();
      const res = await api.company.getAll();
      if (res?.ok && res.data?.data) {
        setCompanies(res.data.data);
      } else if (res?.ok && Array.isArray(res.data)) {
        setCompanies(res.data);
      }
    } catch (e) {
      console.error("Failed to load companies", e);
    }
  }, []);

  useEffect(() => {
    loadData();
    loadStats();
    loadCompanies();
  }, [loadData, loadStats, loadCompanies]);

  // Load Detail
  const openDetail = async (id: number) => {
    setSelectedId(id);
    setDetailLoading(true);
    try {
      const api = createApi();
      const res = await api.adminScm.getById(id);
      if (res?.ok && res.data?.data) {
        setDetail(res.data.data);
        setNewStatus(res.data.data.status);
        setStatusNote("");
      } else if (res?.ok && res.data) {
        setDetail(res.data);
        setNewStatus(res.data.status);
        setStatusNote("");
      }
    } catch (e) {
      console.error("Failed to load SCM detail", e);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedId(null);
    setDetail(null);
  };

  // Update Status
  const handleUpdateStatus = async () => {
    if (!selectedId) return;
    setStatusUpdating(true);
    try {
      const api = createApi();
      const res = await api.adminScm.updateStatus(selectedId, {
        newStatus,
        internalNote: statusNote,
      });
      if (res?.ok) {
        await openDetail(selectedId);
        await loadData();
        await loadStats();
        setStatusNote("");
      }
    } catch (e) {
      console.error("Failed to update status", e);
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleQuickStatus = async (status: number) => {
    if (!selectedId) return;
    setStatusUpdating(true);
    try {
      const api = createApi();
      const res = await api.adminScm.updateStatus(selectedId, {
        newStatus: status,
        internalNote: `Quick action: changed status to ${SCM_STATUS[status]?.label || status}`,
      });
      if (res?.ok) {
        await openDetail(selectedId);
        await loadData();
        await loadStats();
      }
    } catch (e) {
      console.error("Failed to update status", e);
    } finally {
      setStatusUpdating(false);
    }
  };

  // Add Operator Match
  const handleAddMatch = async () => {
    if (!selectedId || !matchCompanyId) return;
    setMatchSaving(true);
    try {
      const api = createApi();
      const res = await api.adminScm.addMatch(selectedId, {
        operatorCompanyId: Number(matchCompanyId),
        notes: matchNotes,
        hasOffer: matchHasOffer,
        offerAmount: matchOfferAmount ? Number(matchOfferAmount) : null,
        offerCurrency: matchOfferCurrency,
      });
      if (res?.ok) {
        setMatchCompanyId("");
        setMatchNotes("");
        setMatchHasOffer(false);
        setMatchOfferAmount("");
        await openDetail(selectedId);
        await loadData();
        await loadStats();
      }
    } catch (e) {
      console.error("Failed to add match", e);
    } finally {
      setMatchSaving(false);
    }
  };

  // Remove Match
  const handleRemoveMatch = async (matchId: number) => {
    if (!selectedId || !window.confirm("Remove this matched operator?")) return;
    try {
      const api = createApi();
      const res = await api.adminScm.deleteMatch(selectedId, matchId);
      if (res?.ok) {
        await openDetail(selectedId);
        await loadData();
        await loadStats();
      }
    } catch (e) {
      console.error("Failed to remove match", e);
    }
  };

  // Page Menus (Dashboard top bar & breadcrumb)
  const outletCtx = useOutletContext<any>();
  const setPageMenus = outletCtx?.setPageMenus;

  useEffect(() => {
    if (setPageMenus) {
      setPageMenus([
        {
          label: "SCM",
          items: [{ label: "Smart Charter Matching", route: "/dashboard/scm" }],
        },
      ]);
    }
    return () => {
      if (setPageMenus) setPageMenus([]);
    };
  }, [setPageMenus]);

  // Table Columns
  const columns = useMemo(
    () => [
      {
        key: "referenceNumber",
        title: "Ref No",
        minWidth: 160,
        accessor: (row: any) => row.referenceNumber,
        render: (row: any) => {
          const isPax = row.charterType === 1;
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {isPax ? (
                <MdFlightTakeoff size={18} color="#3B82F6" />
              ) : (
                <MdLuggage size={18} color="#F97316" />
              )}
              <strong style={{ fontSize: 13, color: "#1E293B" }}>
                {row.referenceNumber}
              </strong>
            </div>
          );
        },
      },
      {
        key: "charterType",
        title: "Type",
        minWidth: 120,
        accessor: (row: any) => row.charterType,
        render: (row: any, val: any) => {
          const t = CHARTER_TYPES[val] || CHARTER_TYPES[1];
          return (
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: t.color,
                background: `${t.color}18`,
                padding: "3px 8px",
                borderRadius: 6,
              }}
            >
              {t.label}
            </span>
          );
        },
      },
      {
        key: "companyName",
        title: "Requester Company",
        minWidth: 200,
        accessor: (row: any) => row.companyName,
        render: (row: any) => (
          <div>
            <div style={{ fontWeight: 600, color: "#0F172A", fontSize: 13 }}>
              {row.companyName || "—"}
            </div>
            <div style={{ fontSize: 11, color: "#64748B" }}>
              {row.contactPerson}
            </div>
          </div>
        ),
      },
      {
        key: "route",
        title: "Route",
        minWidth: 140,
        accessor: (row: any) => `${row.originAirportCode} - ${row.destinationAirportCode}`,
        render: (row: any) => (
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 600 }}>
            <span>{row.originAirportCode}</span>
            <span style={{ color: "#94A3B8" }}>→</span>
            <span>{row.destinationAirportCode}</span>
          </div>
        ),
      },
      {
        key: "operationStartDate",
        title: "Period & Days",
        minWidth: 220,
        accessor: (row: any) => row.operationStartDate,
        render: (row: any) => (
          <div style={{ fontSize: 12 }}>
            <div>
              {fmtDate(row.operationStartDate)} - {fmtDate(row.operationEndDate)}
            </div>
            <div style={{ fontSize: 11, color: "#2563EB", fontWeight: 600 }}>
              {row.weeklyFrequency} flights/wk ({row.operatingDays})
            </div>
          </div>
        ),
      },
      {
        key: "capacitySummary",
        title: "Capacity",
        minWidth: 140,
        accessor: (row: any) => row.capacitySummary,
        render: (row: any, val: any) => (
          <span style={{ fontWeight: 600, color: "#334155", fontSize: 12 }}>
            {val || "—"}
          </span>
        ),
      },
      {
        key: "status",
        title: "Status",
        minWidth: 150,
        accessor: (row: any) => row.status,
        render: (row: any, val: any) => {
          const s = SCM_STATUS[val] || SCM_STATUS[1];
          return <Badge label={s.label} color={s.color} bg={s.bg} />;
        },
      },
      {
        key: "matchedOperatorsCount",
        title: "Operators",
        minWidth: 130,
        accessor: (row: any) => row.matchedOperatorsCount,
        render: (row: any, val: any) => (
          <span
            style={{
              padding: "2px 8px",
              borderRadius: 12,
              background: val > 0 ? "rgba(37,99,235,.1)" : "#F1F5F9",
              color: val > 0 ? "#2563EB" : "#94A3B8",
              fontWeight: 600,
              fontSize: 12,
            }}
          >
            {val || 0} matched
          </span>
        ),
      },
      {
        key: "actions",
        title: "Actions",
        minWidth: 120,
        accessor: (row: any) => row.id,
        render: (row: any) => (
          <Button
            icon={MdVisibility}
            variant="outline"
            size="sm"
            onClick={() => openDetail(row.id)}
          >
            Detail
          </Button>
        ),
      },
    ],
    []
  );

  return (
    <div style={{ padding: 24, background: "#F8FAFC", minHeight: "100vh" }}>
      {/* ─── Header ─── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: "#0F172A",
              margin: 0,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <MdAirplanemodeActive color="#2563EB" size={26} />
            Smart Charter Matching (SCM) Management
          </h1>
          <p style={{ margin: "4px 0 0", color: "#64748B", fontSize: 13 }}>
            Review confidential passenger and cargo charter requests and manage airline matches.
          </p>
        </div>
        <Button
          icon={MdRefresh}
          variant="outline"
          onClick={() => {
            loadData();
            loadStats();
          }}
        >
          Refresh
        </Button>
      </div>

      {/* ─── Stats Cards ─── */}
      {stats && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 14,
            marginBottom: 20,
          }}
        >
          <div style={statCardStyle("#2563EB")}>
            <div style={{ color: "#64748B", fontSize: 12, fontWeight: 600 }}>TOTAL REQUESTS</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: "#0F172A", marginTop: 4 }}>
              {stats.totalRequests}
            </div>
            <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 2 }}>
              {stats.passengerRequestsCount} Pax · {stats.cargoRequestsCount} Cargo
            </div>
          </div>
          <div style={statCardStyle("#3B82F6")}>
            <div style={{ color: "#2563EB", fontSize: 12, fontWeight: 600 }}>NEW REQUESTS</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: "#2563EB", marginTop: 4 }}>
              {stats.newRequests}
            </div>
            <div style={{ fontSize: 11, color: "#64748B", marginTop: 2 }}>Awaiting first review</div>
          </div>
          <div style={statCardStyle("#F59E0B")}>
            <div style={{ color: "#D97706", fontSize: 12, fontWeight: 600 }}>IN MATCHING</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: "#D97706", marginTop: 4 }}>
              {stats.activeMatchingRequests}
            </div>
            <div style={{ fontSize: 11, color: "#64748B", marginTop: 2 }}>Outreach & Offers</div>
          </div>
          <div style={statCardStyle("#16A34A")}>
            <div style={{ color: "#16A34A", fontSize: 12, fontWeight: 600 }}>CLOSED / MATCHED</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: "#16A34A", marginTop: 4 }}>
              {stats.closedRequests}
            </div>
            <div style={{ fontSize: 11, color: "#64748B", marginTop: 2 }}>Successfully concluded</div>
          </div>
        </div>
      )}

      {/* ─── Filters Bar ─── */}
      <div
        style={{
          background: "#FFFFFF",
          padding: 16,
          borderRadius: 12,
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          marginBottom: 16,
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, minWidth: 220 }}>
          <MdSearch size={20} color="#94A3B8" />
          <input
            type="text"
            placeholder="Search Ref No, Airport, Company..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={inputStyle}
          />
        </div>

        {/* Charter Type Filter */}
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value ? Number(e.target.value) : "")}
          style={selectStyle}
        >
          <option value="">All Charter Types</option>
          <option value="1">Passenger Charter</option>
          <option value="2">Cargo Charter</option>
        </select>

        {/* Status Filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value ? Number(e.target.value) : "")}
          style={selectStyle}
        >
          <option value="">All Statuses</option>
          {Object.entries(SCM_STATUS).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label}
            </option>
          ))}
        </select>

        {(typeFilter || statusFilter || searchText) && (
          <Button
            label="Clear Filters"
            variant="ghost"
            size="sm"
            onClick={() => {
              setTypeFilter("");
              setStatusFilter("");
              setSearchText("");
            }}
          />
        )}
      </div>

      {/* ─── Requests Table ─── */}
      <div style={{ background: "#FFFFFF", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
        <Table columns={columns} data={data} loading={loading} />
      </div>

      {/* ─── Detail & Matching Modal ─── */}
      <Modal open={Boolean(selectedId)} fullScreen onClose={closeDetail}>
        {detailLoading || !detail ? (
          <div style={{ padding: 60, textAlign: "center", color: "#64748B" }}>
            <div
              style={{
                width: 36,
                height: 36,
                border: "3px solid #E2E8F0",
                borderTopColor: "#2563EB",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 12px",
              }}
            />
            Loading SCM request details...
          </div>
        ) : (
          <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Modal Header & Title */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingBottom: 14,
                borderBottom: "1px solid #E2E8F0",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {detail.charterType === 1 ? (
                  <MdFlightTakeoff size={28} color="#2563EB" />
                ) : (
                  <MdInventory size={28} color="#F97316" />
                )}
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#0F172A" }}>
                    SCM Request: {detail.referenceNumber}
                  </div>
                  <div style={{ fontSize: 12, color: "#64748B" }}>
                    Created: {fmtDate(detail.createdDate)} · Requester: {detail.companyName}
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <Badge
                  label={SCM_STATUS[detail.status]?.label || "New"}
                  color={SCM_STATUS[detail.status]?.color || "#2563EB"}
                  bg={SCM_STATUS[detail.status]?.bg || "rgba(37,99,235,.15)"}
                />

                {detail.status !== 3 && detail.status !== 7 && (
                  <button
                    onClick={() => handleQuickStatus(3)}
                    disabled={statusUpdating}
                    style={{
                      background: "#F59E0B",
                      color: "#FFFFFF",
                      border: "none",
                      borderRadius: 8,
                      padding: "6px 14px",
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    Start Matching
                  </button>
                )}

                {detail.status !== 7 ? (
                  <button
                    onClick={() => handleQuickStatus(7)}
                    disabled={statusUpdating}
                    style={{
                      background: "#DC2626",
                      color: "#FFFFFF",
                      border: "none",
                      borderRadius: 8,
                      padding: "6px 14px",
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    Close Request
                  </button>
                ) : (
                  <button
                    onClick={() => handleQuickStatus(1)}
                    disabled={statusUpdating}
                    style={{
                      background: "#16A34A",
                      color: "#FFFFFF",
                      border: "none",
                      borderRadius: 8,
                      padding: "6px 14px",
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    Reopen Request
                  </button>
                )}

                <button
                  onClick={closeDetail}
                  style={{
                    background: "#F1F5F9",
                    border: "none",
                    borderRadius: 8,
                    padding: "6px 14px",
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#475569",
                  }}
                >
                  Close
                </button>
              </div>
            </div>

            {/* ── Card 1: Operation & Route Details ── */}
            <Card icon={MdFlightTakeoff} title="Operation & Route Details">
              <InfoRow icon={MdFlightTakeoff} label="Charter Type" value={CHARTER_TYPES[detail.charterType]?.label} />
              <InfoRow icon={MdDateRange} label="Operation Type" value={OP_TYPES[detail.operationType] || "—"} />
              <InfoRow
                icon={MdFlightTakeoff}
                label="Route (Origin → Destination)"
                value={`${detail.originAirportCode} (${detail.originAirportName || "—"}) → ${detail.destinationAirportCode} (${detail.destinationAirportName || "—"})`}
              />
              <InfoRow
                icon={MdDateRange}
                label="Operation Period"
                value={`${fmtDate(detail.operationStartDate)} – ${fmtDate(detail.operationEndDate)}`}
              />
              <InfoRow
                icon={MdSchedule}
                label="Frequency & Operating Days"
                value={`${detail.weeklyFrequency || 0} flights/week (${detail.operatingDays || "—"})`}
              />
              <InfoRow
                icon={MdSchedule}
                label="Departure Preference (Outbound)"
                value={TIME_PREF[detail.outboundDeparturePreference] || "Flexible"}
              />
              {detail.returnDeparturePreference != null && (
                <InfoRow
                  icon={MdSchedule}
                  label="Departure Preference (Return)"
                  value={TIME_PREF[detail.returnDeparturePreference] || "Flexible"}
                />
              )}
              <InfoRow
                icon={MdDateRange}
                label="Date Flexibility"
                value={DATE_FLEX[detail.dateFlexibility] || "Exact Dates (No flexibility)"}
              />
            </Card>

            {/* ── Card 2: Pax or Cargo Specifics ── */}
            {detail.charterType === 1 ? (
              <Card icon={MdPerson} title="Passenger & Cabin Details">
                <InfoRow
                  icon={MdPerson}
                  label="Seats Required per Flight"
                  value={<strong style={{ color: "#2563EB", fontSize: 14 }}>{detail.seatsRequiredPerFlight || "—"} Seats</strong>}
                />
                <InfoRow
                  icon={MdAirplanemodeActive}
                  label="Preferred Aircraft Type"
                  value={detail.preferredAircraftTypeName || detail.preferredAircraftTypeOther || "Any / No Preference"}
                />
                <InfoRow
                  icon={MdLuggage}
                  label="Luggage Allowance per PAX"
                  value={detail.luggageAllowancePerPaxKg ? `${detail.luggageAllowancePerPaxKg} kg` : "Standard Airline Allowance"}
                />
                <InfoRow
                  icon={MdDescription}
                  label="Cabin Service / Catering"
                  value={detail.cateringPreference || "Standard Service"}
                />
              </Card>
            ) : (
              <Card icon={MdInventory} title="Cargo & Loading Details">
                <InfoRow
                  icon={MdInventory}
                  label="Cargo Type"
                  value={
                    <strong>
                      {CARGO_TYPES[detail.cargoType] || "General Cargo"}
                      {detail.cargoTypeOtherExplanation ? ` (${detail.cargoTypeOtherExplanation})` : ""}
                    </strong>
                  }
                />
                <InfoRow
                  icon={MdLuggage}
                  label="Payload / Weight per Flight"
                  value={
                    <strong style={{ color: "#F97316", fontSize: 14 }}>
                      {detail.cargoWeightPerFlight ?? "—"} {WEIGHT_UNITS[detail.cargoWeightUnit] || "kg"}
                    </strong>
                  }
                />
                <InfoRow
                  icon={MdInventory}
                  label="Volume (CBM)"
                  value={detail.cargoVolumeM3 ? `${detail.cargoVolumeM3} m³` : "—"}
                />
                <InfoRow
                  icon={MdInventory}
                  label="Loading Type"
                  value={
                    <span>
                      {LOADING_TYPES[detail.loadingType] || "Loose / Bulk"}
                      {detail.loadingTypeOtherExplanation ? ` (${detail.loadingTypeOtherExplanation})` : ""}
                    </span>
                  }
                />
                <InfoRow
                  icon={MdDescription}
                  label="Special Cargo Requirements"
                  value={SPECIAL_CARGO[detail.specialCargo] || "None"}
                />
                <InfoRow
                  icon={MdDescription}
                  label="Temperature Control"
                  value={
                    detail.temperatureMinDegree != null
                      ? `${detail.temperatureMinDegree}°C to ${detail.temperatureMaxDegree}°C`
                      : "No Temperature Control Required"
                  }
                />
                {(detail.maxPieceLengthM || detail.maxWeightPerPieceKg) && (
                  <InfoRow
                    icon={MdInventory}
                    label="Max Piece Dimensions / Weight"
                    value={`${detail.maxPieceLengthM || 0}m (L) × ${detail.maxPieceWidthM || 0}m (W) × ${detail.maxPieceHeightM || 0}m (H) | Max: ${detail.maxWeightPerPieceKg || 0} kg`}
                  />
                )}
              </Card>
            )}

            {/* ── Card 3: Commercial & Budget Terms ── */}
            <Card icon={MdAttachMoney} title="Commercial & Target Budget Terms">
              <InfoRow
                icon={MdAttachMoney}
                label="Target Budget"
                value={
                  <strong style={{ color: "#16A34A", fontSize: 14 }}>
                    {fmtMoney(detail.targetBudgetAmount, detail.targetBudgetCurrency || "USD", detail.targetBudgetRateBasis)}
                  </strong>
                }
              />
              <InfoRow
                icon={MdAttachMoney}
                label="Rate Basis"
                value={RATE_BASIS[detail.targetBudgetRateBasis] || "—"}
              />
              <InfoRow
                icon={MdAttachMoney}
                label="Payment Terms"
                value={detail.paymentTerms || "Standard Charter Contract"}
              />
              <InfoRow
                icon={MdDescription}
                label="Commissionable / YAC Agency"
                value={<BoolBadge value={detail.isCommissionable} />}
              />
              <InfoRow
                icon={MdFlightTakeoff}
                label="Return Ferry Acceptable"
                value={<BoolBadge value={detail.hasReturnFerry} />}
              />
            </Card>

            {/* ── Card 4: Requester Company Info (Confidential to FIXER Admin) ── */}
            <Card icon={MdBusiness} title="Requester Company Information (Confidential to FIXER Admin)">
              <InfoRow icon={MdBusiness} label="Company Name" value={<strong>{detail.companyName}</strong>} />
              <InfoRow icon={MdPerson} label="Contact Person" value={detail.contactPerson || "—"} />
              <InfoRow
                icon={MdEmail}
                label="Contact Email"
                value={<a href={`mailto:${detail.contactEmail}`} style={{ color: "#2563EB", fontWeight: 600 }}>{detail.contactEmail}</a>}
              />
              <InfoRow
                icon={MdPhone}
                label="Contact Phone"
                value={detail.contactPhone ? <a href={`tel:${detail.contactPhone}`} style={{ color: "#2563EB", fontWeight: 600 }}>{detail.contactPhone}</a> : "—"}
              />
              {detail.additionalRequirements && (
                <InfoRow
                  icon={MdDescription}
                  label="Additional Notes / Notes"
                  value={<div style={{ whiteSpace: "pre-wrap" }}>{detail.additionalRequirements}</div>}
                />
              )}
            </Card>

            {/* ── Card: Uploaded Documents & Attachments ── */}
            {(() => {
              const fileList: any[] = [
                ...(detail.documents || []),
                ...(detail.images || []),
                ...(detail.medias || []).filter(
                  (m: any) =>
                    !(detail.documents || []).some((d: any) => d.id === m.id) &&
                    !(detail.images || []).some((i: any) => i.id === m.id)
                ),
              ];

              return (
                <Card
                  icon={MdAttachFile}
                  title={`Uploaded Documents & Attachments (${fileList.length})`}
                >
                  {fileList.length > 0 ? (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                        gap: 14,
                      }}
                    >
                      {fileList.map((f: any, idx: number) => {
                        const fileUrl = getDocUrl(f);
                        const isImg =
                          f.mediaType === 1 ||
                          /\.(jpeg|jpg|png|webp|gif)$/i.test(fileUrl || f.fileName || "");
                        const isPdf =
                          f.extension?.toLowerCase() === ".pdf" ||
                          /\.pdf$/i.test(fileUrl || f.fileName || "");

                        return (
                          <div
                            key={f.id || idx}
                            onClick={() => fileUrl && setPreview(fileUrl)}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 12,
                              padding: "12px 14px",
                              background: "#F8FAFC",
                              border: "1px solid #E2E8F0",
                              borderRadius: 12,
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = "#2563EB";
                              e.currentTarget.style.background = "#EFF6FF";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = "#E2E8F0";
                              e.currentTarget.style.background = "#F8FAFC";
                            }}
                          >
                            <div
                              style={{
                                width: 44,
                                height: 44,
                                borderRadius: 10,
                                background: isPdf ? "#FEE2E2" : isImg ? "#E0F2FE" : "#F1F5F9",
                                color: isPdf ? "#DC2626" : isImg ? "#0284C7" : "#475569",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              {isPdf ? (
                                <MdPictureAsPdf size={28} />
                              ) : isImg ? (
                                <MdImage size={28} />
                              ) : (
                                <MdInsertDriveFile size={28} />
                              )}
                            </div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div
                                style={{
                                  fontWeight: 600,
                                  fontSize: 13,
                                  color: "#0F172A",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                title={f.fileName || fileUrl}
                              >
                                {f.fileName || fileUrl?.split("/").pop() || "Document"}
                              </div>
                              <div
                                style={{
                                  fontSize: 11,
                                  color: "#2563EB",
                                  fontWeight: 600,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                  marginTop: 3,
                                }}
                              >
                                <span>Click to Preview</span>
                                <MdOpenInNew size={12} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div
                      style={{
                        padding: "24px 16px",
                        textAlign: "center",
                        color: "#94A3B8",
                        fontSize: 13,
                        background: "#F8FAFC",
                        borderRadius: 8,
                        border: "1px dashed #CBD5E1",
                      }}
                    >
                      <MdAttachFile size={26} style={{ marginBottom: 6, opacity: 0.5 }} />
                      <div>No documents or files uploaded for this request.</div>
                    </div>
                  )}
                </Card>
              );
            })()}

            {/* ── Card 5: Status & Internal Operational Notes ── */}
            <Card icon={MdCheckCircle} title="Update Status & Operational Notes">
              <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(Number(e.target.value))}
                  style={{ ...selectStyle, fontWeight: 600, minWidth: 200 }}
                >
                  {Object.entries(SCM_STATUS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v.label}
                    </option>
                  ))}
                </select>

                <input
                  type="text"
                  placeholder="Internal note (e.g. Sent RFQ to 3 operators, awaiting confirmation)..."
                  value={statusNote}
                  onChange={(e) => setStatusNote(e.target.value)}
                  style={{ ...inputStyle, flex: 1, minWidth: 260 }}
                />

                <Button
                  variant="primary"
                  onClick={handleUpdateStatus}
                  disabled={statusUpdating}
                >
                  {statusUpdating ? "Saving..." : "Update Status"}
                </Button>
              </div>

              {detail.internalNotes && (
                <div style={{ padding: 12, background: "#F8FAFC", borderRadius: 8, fontSize: 12, color: "#475569", border: "1px solid #E2E8F0" }}>
                  <strong style={{ color: "#1E293B" }}>Internal Note History:</strong>
                  <div style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>{detail.internalNotes}</div>
                </div>
              )}
            </Card>

            {/* ── Card 6: Operator Matches & Outreach (Secret Matching) ── */}
            <Card icon={MdOutlineHandshake} title={`Matched Airline Operators (${detail.operatorMatches?.length || 0})`}>
              {detail.operatorMatches && detail.operatorMatches.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                  {detail.operatorMatches.map((m: any) => (
                    <div
                      key={m.id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "10px 14px",
                        background: "#F8FAFC",
                        borderRadius: 8,
                        border: "1px solid #E2E8F0",
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13, color: "#0F172A" }}>
                          {m.operatorCompanyName}
                        </div>
                        <div style={{ fontSize: 11, color: "#64748B" }}>
                          Matched Date: {fmtDate(m.contactedAt)} · {m.notes || "No notes"}
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        {m.hasOffer ? (
                          <span style={{ fontSize: 12, fontWeight: 700, color: "#16A34A" }}>
                            Offer: {m.offerCurrency} {Number(m.offerAmount).toLocaleString()}
                          </span>
                        ) : (
                          <span style={{ fontSize: 11, color: "#94A3B8" }}>No offer yet</span>
                        )}
                        <button
                          onClick={() => handleRemoveMatch(m.id)}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#EF4444" }}
                          title="Remove operator match"
                        >
                          <MdDeleteOutline size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ fontSize: 12, color: "#94A3B8", fontStyle: "italic", margin: "0 0 16px" }}>
                  No operators matched yet. Select a verified airline below to initiate confidential matching.
                </p>
              )}

              {/* Add Operator Match Form */}
              <div style={{ background: "#F1F5F9", padding: 14, borderRadius: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#334155", marginBottom: 8 }}>
                  + Match New Operator (Confidential Outreach)
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                  <select
                    value={matchCompanyId}
                    onChange={(e) => setMatchCompanyId(e.target.value ? Number(e.target.value) : "")}
                    style={selectStyle}
                  >
                    <option value="">Select Verified Airline / Operator Company...</option>
                    {companies.map((c: any) => (
                      <option key={c.id} value={c.id}>
                        {c.companyName}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="Outreach note (e.g. Sent anonymous route RFQ)..."
                    value={matchNotes}
                    onChange={(e) => setMatchNotes(e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#334155" }}>
                    <input
                      type="checkbox"
                      checked={matchHasOffer}
                      onChange={(e) => setMatchHasOffer(e.target.checked)}
                    />
                    Has Received Offer
                  </label>
                  {matchHasOffer && (
                    <>
                      <input
                        type="number"
                        placeholder="Offer Amount"
                        value={matchOfferAmount}
                        onChange={(e) => setMatchOfferAmount(e.target.value ? Number(e.target.value) : "")}
                        style={{ ...inputStyle, width: 140 }}
                      />
                      <select
                        value={matchOfferCurrency}
                        onChange={(e) => setMatchOfferCurrency(e.target.value)}
                        style={{ ...selectStyle, width: 90 }}
                      >
                        <option value="EUR">EUR</option>
                        <option value="USD">USD</option>
                        <option value="GBP">GBP</option>
                      </select>
                    </>
                  )}
                  <Button
                    icon={MdAdd}
                    variant="primary"
                    size="sm"
                    onClick={handleAddMatch}
                    disabled={!matchCompanyId || matchSaving}
                  >
                    {matchSaving ? "Adding..." : "Add Operator"}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </Modal>

      {/* ─── Document / Image Preview Modal (Like UserForms) ─── */}
      {preview && (
        <Modal open onClose={() => setPreview(null)} width="90vw">
          <div style={{ position: "relative" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingBottom: 12,
                marginBottom: 14,
                borderBottom: "1px solid #E2E8F0",
              }}
            >
              <strong style={{ fontSize: 15, color: "#0F172A" }}>Document Preview</strong>
              <button
                onClick={() => setPreview(null)}
                style={{
                  background: "#F1F5F9",
                  border: "none",
                  borderRadius: 8,
                  padding: "6px 14px",
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#475569",
                }}
              >
                Close Preview
              </button>
            </div>
            {preview.toLowerCase().includes(".pdf") ? (
              <iframe
                src={preview}
                style={{ width: "100%", height: "80vh", border: "1px solid #E2E8F0", borderRadius: 8 }}
                title="Document Preview"
              />
            ) : (
              <div style={{ textAlign: "center", background: "#0F172A", padding: 16, borderRadius: 8 }}>
                <img
                  src={preview}
                  style={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain", borderRadius: 4 }}
                  alt="Preview"
                />
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

// ─── Style Helpers ────────────────────────────────────────────────────────────
const statCardStyle = (accent: string): React.CSSProperties => ({
  background: "#FFFFFF",
  borderRadius: 12,
  padding: 16,
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  borderTop: `3px solid ${accent}`,
});

const sectionBoxStyle: React.CSSProperties = {
  background: "#FFFFFF",
  borderRadius: 8,
  padding: 14,
  border: "1px solid #E2E8F0",
};

const sectionHeaderStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  color: "#0F172A",
  margin: "0 0 10px 0",
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const labelStyle: React.CSSProperties = {
  color: "#64748B",
  fontWeight: 500,
  display: "inline-block",
  minWidth: 120,
};

const inputStyle: React.CSSProperties = {
  padding: "8px 12px",
  borderRadius: 6,
  border: "1px solid #CBD5E1",
  fontSize: 13,
  outline: "none",
  background: "#FFFFFF",
};

const selectStyle: React.CSSProperties = {
  padding: "8px 12px",
  borderRadius: 6,
  border: "1px solid #CBD5E1",
  fontSize: 13,
  outline: "none",
  background: "#FFFFFF",
};

export default AdminScmList;
